import { CapabilityButton } from "../CapabilityButton";
import { permissionNames as P } from "../../auth/permissions.generated";
import { useState, useEffect } from 'react';
import { Button } from '../ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../ui/tabs';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Checkbox } from '../ui/checkbox';
import { ScrollArea } from '../ui/scroll-area';
import { Badge } from '../ui/badge';
import { Alert, AlertDescription } from '../ui/alert';
import { Progress } from '../ui/progress';
import { Textarea } from '../ui/textarea';
import { 
  ArrowLeft, 
  Upload, 
  FileText, 
  Package, 
  Loader2, 
  Trash2, 
  Download,
  CheckSquare,
  Square,
  Search,
  Folder,
  FileSpreadsheet,
  AlertCircle,
  Play,
  Clock,
  CheckCircle,
  XCircle,
  RefreshCw,
  ExternalLink,
  X,
  Plus
} from 'lucide-react';
import { type NavigationItem } from '../Navigation';
import { useAzureFunctionsApiService, useAzureFunctionsCatalogApi, type CatalogJob, type CatalogTemplate } from '../../services/apiService';
import { useAuthenticatedUser } from '../../hooks/useAuth';
import { toast } from 'sonner';
import { cn } from '../ui/utils';

interface CatalogManagementProps {
  onNavigate: (view: NavigationItem) => void;
}

interface QueryInfo {
  queryId: string;
  queryName: string;
  parentFolder: string;
}

interface EntitySelection {
  entityId: number;
  itemNumber: string;
  name: string;
  itemName?: string;
  entityType: string;
  isSelected: boolean;
  thumbnailUrl?: string;
  mainImage?: string;
}

export default function CatalogManagement({ onNavigate }: CatalogManagementProps) {
  const { isAuthenticated } = useAuthenticatedUser();
  const api = useAzureFunctionsApiService();
  const catalogApi = useAzureFunctionsCatalogApi();
  
  // State for queries
  const [queryGroups, setQueryGroups] = useState<{ folder: string; queries: QueryInfo[] }[]>([]);
  const [selectedQuery, setSelectedQuery] = useState<string | null>(null);
  const [loadingQueries, setLoadingQueries] = useState(false);
  const [querySearchTerm, setQuerySearchTerm] = useState('');
  
  // State for entities
  const [entities, setEntities] = useState<EntitySelection[]>([]);
  const [loadingEntities, setLoadingEntities] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  
  // State for templates - updated to use CatalogTemplate type
  const [templates, setTemplates] = useState<CatalogTemplate[]>([]);
  const [selectedTemplateId, setSelectedTemplateId] = useState<number | null>(null);
  const [templateSearchTerm, setTemplateSearchTerm] = useState('');
  const [loadingTemplates, setLoadingTemplates] = useState(false);
  const [uploadingTemplate, setUploadingTemplate] = useState(false);
  
  // Template upload dialog state
  const [showUploadDialog, setShowUploadDialog] = useState(false);
  const [uploadForm, setUploadForm] = useState({
    name: '',
    description: '',
    tags: [] as string[],
    file: null as File | null
  });
  const [tagInput, setTagInput] = useState('');
  
  // State for generation
  const [generatingCatalog, setGeneratingCatalog] = useState(false);
  
  // State for jobs
  const [catalogJobs, setCatalogJobs] = useState<CatalogJob[]>([]);
  const [loadingJobs, setLoadingJobs] = useState(false);
  const [activeTab, setActiveTab] = useState('browse');
  
  // Add state for showing auth error details
  const [authError, setAuthError] = useState<string | null>(null);
  
  // Load queries, templates, and jobs on component mount - only when authenticated
  useEffect(() => {
    if (!isAuthenticated) {
      console.log('User not authenticated, skipping data load');
      setAuthError('You must be logged in to access catalog management.');
      return;
    }
    
    // Add a small delay to ensure token is available
    const timeoutId = setTimeout(async () => {
      try {
        // Test if user has required permissions by making a simple API call
        console.log('[CatalogManagement] Testing API access...');
        await loadQueries();
        await loadTemplates();
        await loadCatalogJobs();
        setAuthError(null); // Clear any previous errors
      } catch (error) {
        console.error('[CatalogManagement] Failed to load initial data:', error);
        
        // Check if it's an auth error
        if (error instanceof Error && error.message.includes('401')) {
          setAuthError(
            'Access Denied: You do not have the required permissions (product:pdfs) to access catalog management. ' +
            'Please contact your administrator to grant you access.'
          );
        } else {
          setAuthError(`Failed to load catalog data: ${error instanceof Error ? error.message : 'Unknown error'}`);
        }
      }
    }, 100);
    
    return () => clearTimeout(timeoutId);
  }, [isAuthenticated]);
  
  // Auto-refresh jobs when on jobs tab
  useEffect(() => {
    if (!isAuthenticated) {
      return;
    }
    
    if (activeTab === 'jobs') {
      loadCatalogJobs();
      const interval = setInterval(() => {
        loadCatalogJobs();
      }, 5000); // Refresh every 5 seconds
      
      return () => clearInterval(interval);
    }
  }, [activeTab, isAuthenticated]);
  
  const loadQueries = async () => {
    if (!isAuthenticated) {
      console.log('Cannot load queries - user not authenticated');
      return;
    }
    
    try {
      setLoadingQueries(true);
      const response = await api.get<{
        totalQueries: number;
        queryGroups: { folder: string; queries: QueryInfo[] }[];
      }>('/inriver/queries');
      
      // Check if response has data and queryGroups
      console.log('[loadQueries] Response:', response);
      
      if (!response || !response.queryGroups || !Array.isArray(response.queryGroups)) {
        console.error('[loadQueries] Invalid response structure:', response);
        toast.error('Invalid response from server');
        setQueryGroups([]);
        return;
      }
      
      // Sort query groups alphabetically by folder name
      const sortedGroups = response.queryGroups.map(group => ({
        ...group,
        // Sort queries within each group alphabetically by queryName
        queries: [...(group.queries || [])].sort((a, b) => 
          a.queryName.localeCompare(b.queryName)
        )
      })).sort((a, b) => a.folder.localeCompare(b.folder));
      
      setQueryGroups(sortedGroups);
      toast.success(`Loaded ${response.totalQueries} queries`);
    } catch (error) {
      console.error('Failed to load queries:', error);
      toast.error('Failed to load InRiver queries');
      setQueryGroups([]);
    } finally {
      setLoadingQueries(false);
    }
  };
  
  const loadQueryEntities = async (queryName: string) => {
    if (!isAuthenticated) {
      console.log('Cannot load query entities - user not authenticated');
      return;
    }
    
    try {
      setLoadingEntities(true);
      setSelectedQuery(queryName);
      
      const response = await api.get<{
        queryName: string;
        totalEntities: number;
        entities: EntitySelection[];
      }>(`/inriver/queries/${encodeURIComponent(queryName)}/entities`);
      
      setEntities(response.entities);
      toast.success(`Loaded ${response.totalEntities} items from ${queryName}`);
    } catch (error) {
      console.error('Failed to load query entities:', error);
      toast.error('Failed to load items from query');
      setEntities([]);
    } finally {
      setLoadingEntities(false);
    }
  };
  
  const loadCatalogJobs = async () => {
    if (!isAuthenticated) {
      console.log('Cannot load catalog jobs - user not authenticated');
      return;
    }
    
    try {
      setLoadingJobs(true);
      const jobs = await catalogApi.getCatalogJobs();
      
      setCatalogJobs(jobs.sort((a, b) => 
        new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
      ));
    } catch (error) {
      console.error('Failed to load catalog jobs:', error);
      
      // Check for authentication errors
      if (error instanceof Error) {
        if (error.message.includes('401') || error.message.includes('Authentication required')) {
          // Only show auth error on initial load, not during auto-refresh
          if (!catalogJobs.length) {
            setAuthError(
              'Authentication failed when loading jobs. You may not have the required permissions (product:pdfs). ' +
              'Please try logging out and back in, or contact your administrator.'
            );
            toast.error('Authentication failed - Missing permissions');
          }
        } else if (error.message.includes('403') || error.message.includes('Insufficient permissions')) {
          if (!catalogJobs.length) {
            setAuthError(
              'Access Denied: You do not have the required permissions to access catalog jobs. ' +
              'Required permission: product:pdfs. Please contact your administrator.'
            );
            toast.error('Access denied - Insufficient permissions');
          }
        } else {
          // Don't show error toast for auto-refresh failures
          if (!catalogJobs.length) {
            toast.error('Failed to load catalog jobs');
          }
        }
      }
      
      setCatalogJobs([]);
    } finally {
      setLoadingJobs(false);
    }
  };
  
  const loadTemplates = async () => {
    if (!isAuthenticated) {
      console.log('Cannot load templates - user not authenticated');
      return;
    }
    
    try {
      setLoadingTemplates(true);
      const loadedTemplates = await catalogApi.getTemplates();
      
      setTemplates(loadedTemplates.filter(t => t.isActive));
      toast.success(`Loaded ${loadedTemplates.length} templates`);
    } catch (error) {
      console.error('Failed to load templates:', error);
      
      // Check for authentication errors
      if (error instanceof Error) {
        if (error.message.includes('401') || error.message.includes('Authentication required')) {
          setAuthError(
            'Authentication failed when loading templates. You may not have the required permissions (product:pdfs). ' +
            'Please try logging out and back in, or contact your administrator.'
          );
          toast.error('Authentication failed - Missing permissions');
        } else if (error.message.includes('403') || error.message.includes('Insufficient permissions')) {
          setAuthError(
            'Access Denied: You do not have the required permissions to access templates. ' +
            'Required permission: product:pdfs. Please contact your administrator.'
          );
          toast.error('Access denied - Insufficient permissions');
        } else {
          toast.error('Failed to load templates');
        }
      } else {
        toast.error('Failed to load templates');
      }
      
      setTemplates([]);
    } finally {
      setLoadingTemplates(false);
    }
  };
  
  const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;
    
    // Validate file type
    if (!file.name.toLowerCase().endsWith('.pptx')) {
      toast.error('Please select a PowerPoint file (.pptx)');
      event.target.value = '';
      return;
    }
    
    // Validate file size (max 50MB)
    if (file.size > 50 * 1024 * 1024) {
      toast.error('File size must be less than 50MB');
      event.target.value = '';
      return;
    }
    
    // Set file and auto-fill name from filename
    setUploadForm(prev => ({
      ...prev,
      file,
      name: prev.name || file.name.replace('.pptx', '')
    }));
    
    setShowUploadDialog(true);
  };
  
  const handleUploadTemplate = async () => {
    if (!uploadForm.file) {
      toast.error('Please select a file');
      return;
    }
    
    if (!uploadForm.name.trim()) {
      toast.error('Please enter a template name');
      return;
    }
    
    try {
      setUploadingTemplate(true);
      
      const template = await catalogApi.uploadTemplate(
        uploadForm.name.trim(),
        uploadForm.description.trim() || undefined,
        uploadForm.file,
        uploadForm.tags.length > 0 ? uploadForm.tags : undefined
      );
      
      setTemplates(prev => [template, ...prev]);
      toast.success(`Template "${template.name}" uploaded successfully`);
      
      // Reset form and close dialog
      setShowUploadDialog(false);
      setUploadForm({
        name: '',
        description: '',
        tags: [],
        file: null
      });
      setTagInput('');
      
      // Reset file input
      const fileInput = document.getElementById('template-upload') as HTMLInputElement;
      if (fileInput) fileInput.value = '';
    } catch (error) {
      console.error('Failed to upload template:', error);
      toast.error('Failed to upload template');
    } finally {
      setUploadingTemplate(false);
    }
  };
  
  const handleAddTag = () => {
    const tag = tagInput.trim();
    if (!tag) return;
    
    if (uploadForm.tags.includes(tag)) {
      toast.error('Tag already added');
      return;
    }
    
    setUploadForm(prev => ({
      ...prev,
      tags: [...prev.tags, tag]
    }));
    setTagInput('');
  };
  
  const handleRemoveTag = (tag: string) => {
    setUploadForm(prev => ({
      ...prev,
      tags: prev.tags.filter(t => t !== tag)
    }));
  };
  
  const handleCancelUpload = () => {
    setShowUploadDialog(false);
    setUploadForm({
      name: '',
      description: '',
      tags: [],
      file: null
    });
    setTagInput('');
    
    // Reset file input
    const fileInput = document.getElementById('template-upload') as HTMLInputElement;
    if (fileInput) fileInput.value = '';
  };
  
  const getFilteredTemplates = () => {
    if (!templateSearchTerm) return templates;
    
    const term = templateSearchTerm.toLowerCase();
    return templates.filter(t => 
      t.name.toLowerCase().includes(term) ||
      t.description?.toLowerCase().includes(term) ||
      t.tags?.some(tag => tag.toLowerCase().includes(term))
    );
  };
  
  const handleSelectTemplate = (templateId: number) => {
    setSelectedTemplateId(prev => prev === templateId ? null : templateId);
  };
  
  const handleDeleteTemplate = async (template: CatalogTemplate) => {
    if (!confirm(`Delete template "${template.name}"? This action cannot be undone.`)) {
      return;
    }
    
    try {
      const hardDelete = template.usageCount === 0;
      await catalogApi.deleteTemplate(template.id, hardDelete);
      setTemplates(prev => prev.filter(t => t.id !== template.id));
      
      if (selectedTemplateId === template.id) {
        setSelectedTemplateId(null);
      }
      
      toast.success(`Template "${template.name}" deleted`);
    } catch (error) {
      console.error('Failed to delete template:', error);
      toast.error('Failed to delete template');
    }
  };
  
  // Filter queries based on search term
  const getFilteredQueryGroups = () => {
    if (!querySearchTerm) return queryGroups;
    
    const term = querySearchTerm.toLowerCase();
    return queryGroups.map(group => ({
      ...group,
      queries: group.queries.filter(q => 
        q.queryName.toLowerCase().includes(term) ||
        q.parentFolder.toLowerCase().includes(term)
      )
    })).filter(group => group.queries.length > 0);
  };
  
  const toggleEntitySelection = (entityId: number) => {
    setEntities(prev =>
      prev.map(e =>
        e.entityId === entityId ? { ...e, isSelected: !e.isSelected } : e
      )
    );
  };
  
  const toggleSelectAll = () => {
    const filteredEntities = getFilteredEntities();
    const allSelected = filteredEntities.every(e => e.isSelected);
    
    setEntities(prev =>
      prev.map(e => {
        const inFiltered = filteredEntities.find(fe => fe.entityId === e.entityId);
        return inFiltered ? { ...e, isSelected: !allSelected } : e;
      })
    );
  };
  
  const getFilteredEntities = () => {
    if (!searchTerm) return entities;
    
    const term = searchTerm.toLowerCase();
    return entities.filter(e =>
      e.itemNumber.toLowerCase().includes(term) ||
      e.name.toLowerCase().includes(term)
    );
  };
  
  const handleGenerateCatalog = async () => {
    const selectedEntities = entities.filter(e => e.isSelected);
    
    if (selectedEntities.length === 0) {
      toast.error('Please select at least one item');
      return;
    }
    
    if (!selectedTemplateId) {
      toast.error('Please select a PowerPoint template');
      return;
    }
    
    if (!selectedQuery) {
      toast.error('Please select a query first');
      return;
    }
    
    try {
      setGeneratingCatalog(true);
      
      const selectedTemplate = templates.find(t => t.id === selectedTemplateId);
      if (!selectedTemplate) {
        toast.error('Selected template not found');
        return;
      }
      
      const entityIds = selectedEntities.map(e => e.entityId);
      const catalogName = `${selectedQuery} - ${new Date().toLocaleDateString()}`;
      
      const response = await catalogApi.startCatalogGeneration({
        name: catalogName,
        queryName: selectedQuery,
        templateId: selectedTemplate.blobUrl, // Use blob URL as template ID
        entityIds: entityIds
      });
      
      toast.success(`Started generating catalog with ${entityIds.length} items using template "${selectedTemplate.name}"`);
      
      // Switch to jobs tab to show progress
      setActiveTab('jobs');
      
      // Reload jobs and templates to show updated usage count
      await loadCatalogJobs();
      await loadTemplates();
      
    } catch (error) {
      console.error('Failed to generate catalog:', error);
      toast.error('Failed to start catalog generation');
    } finally {
      setGeneratingCatalog(false);
    }
  };
  
  const handleDownloadCatalog = async (job: CatalogJob) => {
    try {
      toast.info('Downloading catalog...');
      
      const blob = await catalogApi.downloadCatalog(job.id);
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `${job.name}.pptx`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      
      toast.success('Catalog downloaded successfully');
    } catch (error) {
      console.error('Failed to download catalog:', error);
      toast.error('Failed to download catalog');
    }
  };
  
  const handleDeleteJob = async (job: CatalogJob) => {
    try {
      await catalogApi.deleteCatalogJob(job.id);
      setCatalogJobs(prev => prev.filter(j => j.id !== job.id));
      toast.success('Job deleted successfully');
    } catch (error) {
      console.error('Failed to delete job:', error);
      toast.error('Failed to delete job');
    }
  };
  
  const getStatusIcon = (status: CatalogJob['status']) => {
    switch (status) {
      case 'pending':
        return <Clock className="w-4 h-4 text-yellow-500" />;
      case 'processing':
        return <Loader2 className="w-4 h-4 text-blue-500 animate-spin" />;
      case 'completed':
        return <CheckCircle className="w-4 h-4 text-green-500" />;
      case 'completed_with_errors':
        return <AlertCircle className="w-4 h-4 text-orange-500" />;
      case 'failed':
        return <XCircle className="w-4 h-4 text-red-500" />;
      default:
        return <Clock className="w-4 h-4 text-gray-500" />;
    }
  };
  
  const getStatusBadgeVariant = (status: CatalogJob['status']) => {
    switch (status) {
      case 'completed':
        return 'default';
      case 'processing':
        return 'secondary';
      case 'failed':
        return 'destructive';
      default:
        return 'outline';
    }
  };
  
  const selectedCount = entities.filter(e => e.isSelected).length;
  const filteredEntities = getFilteredEntities();
  const allFilteredSelected = filteredEntities.length > 0 && filteredEntities.every(e => e.isSelected);
  const filteredQueryGroups = getFilteredQueryGroups();
  
  return (
    <div className="min-h-screen bg-[#f8f5f0] p-6">
      <div className="max-w-7xl mx-auto space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">            
            <div>
              <h1 className="text-2xl font-semibold text-gray-900">Catalog Management</h1>
              <p className="text-gray-600 mt-1">
                Browse InRiver queries, select items, and generate PowerPoint catalogs
              </p>
            </div>
          </div>
        </div>

        {/* Show authentication error if present */}
        {authError && (
          <Alert className="border-red-200 bg-red-50">
            <AlertCircle className="h-4 w-4 text-red-600" />
            <AlertDescription className="text-red-800">
              <strong>Authentication Error:</strong> {authError}
              <div className="mt-2 text-sm">
                <p>Required permission: <code className="bg-red-100 px-1 rounded">product:pdfs</code></p>
                <p className="mt-1">Please ensure you have been granted this permission in Auth0.</p>
              </div>
            </AlertDescription>
          </Alert>
        )}

        {/* Info Alert */}
        {!authError && (
          <Alert className="bg-blue-50 border-blue-200">
            <AlertCircle className="h-4 w-4 text-blue-600" />
            <AlertDescription className="text-blue-800">
              <strong>How it works:</strong> Upload a PowerPoint template, browse InRiver queries, 
              select items, and generate a customized catalog presentation.
            </AlertDescription>
          </Alert>
        )}

        <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-4">
          <TabsList className="grid w-full grid-cols-4">
            <TabsTrigger value="browse" className="flex items-center gap-2">
              <Search className="w-4 h-4" />
              Browse Queries
            </TabsTrigger>
            <TabsTrigger value="templates" className="flex items-center gap-2">
              <FileText className="w-4 h-4" />
              Templates ({templates.length})
            </TabsTrigger>
            <TabsTrigger value="generate" className="flex items-center gap-2">
              <Package className="w-4 h-4" />
              Generate ({selectedCount} selected)
            </TabsTrigger>
            <TabsTrigger value="jobs" className="flex items-center gap-2">
              <Clock className="w-4 h-4" />
              Jobs ({catalogJobs.length})
            </TabsTrigger>
          </TabsList>

          {/* Browse Queries Tab */}
          <TabsContent value="browse" className="space-y-4">
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
              {/* Queries Panel */}
              <Card className="lg:col-span-1">
                <CardHeader>
                  <div className="flex items-center justify-between">
                    <CardTitle className="flex items-center gap-2">
                      <Folder className="w-5 h-5" />
                      Queries
                    </CardTitle>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={loadQueries}
                      disabled={loadingQueries}
                    >
                      {loadingQueries ? (
                        <Loader2 className="w-4 h-4 animate-spin" />
                      ) : (
                        'Refresh'
                      )}
                    </Button>
                  </div>
                  {/* Query Search */}
                  <div className="mt-4">
                    <Input
                      placeholder="Search queries..."
                      value={querySearchTerm}
                      onChange={(e) => setQuerySearchTerm(e.target.value)}
                      className="w-full"
                    />
                  </div>
                </CardHeader>
                <CardContent>
                  <ScrollArea className="h-[600px]">
                    {loadingQueries ? (
                      <div className="flex items-center justify-center p-8">
                        <Loader2 className="w-6 h-6 animate-spin text-gray-400" />
                      </div>
                    ) : filteredQueryGroups.length === 0 ? (
                      <div className="text-center p-8 text-gray-500">
                        <FileSpreadsheet className="w-12 h-12 mx-auto mb-3 text-gray-300" />
                        <p>No queries found</p>
                      </div>
                    ) : (
                      <div className="space-y-4">
                        {filteredQueryGroups.map((group) => (
                          <div key={group.folder} className="space-y-2">
                            <h3 className="font-medium text-sm text-gray-700 px-2">
                              {group.folder}
                            </h3>
                            <div className="space-y-1">
                              {group.queries.map((query) => (
                                <Button
                                  key={query.queryId}
                                  variant={selectedQuery === query.queryName ? 'default' : 'ghost'}
                                  size="sm"
                                  className="w-full justify-start text-left"
                                  onClick={() => loadQueryEntities(query.queryName)}
                                >
                                  {query.queryName}
                                </Button>
                              ))}
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </ScrollArea>
                </CardContent>
              </Card>

              {/* Items Panel */}
              <Card className="lg:col-span-2">
                <CardHeader>
                  <div className="flex items-center justify-between">
                    <CardTitle className="flex items-center gap-2">
                      <Package className="w-5 h-5" />
                      Items {selectedQuery && `(${selectedQuery})`}
                    </CardTitle>
                    {entities.length > 0 && (
                      <div className="flex items-center gap-2">
                        <Badge variant="outline">
                          {selectedCount} of {entities.length} selected
                        </Badge>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={toggleSelectAll}
                        >
                          {allFilteredSelected ? (
                            <>
                              <Square className="w-4 h-4 mr-1" />
                              Deselect All
                            </>
                          ) : (
                            <>
                              <CheckSquare className="w-4 h-4 mr-1" />
                              Select All
                            </>
                          )}
                        </Button>
                      </div>
                    )}
                  </div>
                  {entities.length > 0 && (
                    <div className="mt-4">
                      <Input
                        placeholder="Search by item number or name..."
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        className="max-w-md"
                      />
                    </div>
                  )}
                </CardHeader>
                <CardContent>
                  {loadingEntities ? (
                    <div className="flex items-center justify-center p-12">
                      <div className="text-center">
                        <Loader2 className="w-8 h-8 animate-spin text-blue-500 mx-auto mb-3" />
                        <p className="text-gray-600">Loading items...</p>
                      </div>
                    </div>
                  ) : !selectedQuery ? (
                    <div className="text-center p-12 text-gray-500">
                      <Search className="w-12 h-12 mx-auto mb-3 text-gray-300" />
                      <p>Select a query to view items</p>
                    </div>
                  ) : entities.length === 0 ? (
                    <div className="text-center p-12 text-gray-500">
                      <Package className="w-12 h-12 mx-auto mb-3 text-gray-300" />
                      <p>No items found in this query</p>
                    </div>
                  ) : (
                    <ScrollArea className="h-[600px]">
                      <div className="space-y-2">
                        {filteredEntities.map((entity) => (
                          <div
                            key={entity.entityId}
                            className="flex items-center gap-3 p-3 rounded-lg border border-gray-200 hover:bg-gray-50 transition-colors cursor-pointer"
                            onClick={() => toggleEntitySelection(entity.entityId)}
                          >
                            <Checkbox
                              checked={entity.isSelected}
                              onCheckedChange={() => toggleEntitySelection(entity.entityId)}
                              onClick={(e) => e.stopPropagation()}
                            />
                            {/* Thumbnail Image */}
                            {entity.mainImage && (
                              <div className="flex-shrink-0 w-12 h-12 bg-gray-100 rounded overflow-hidden">
                                <img 
                                  src={entity.mainImage + "/preview"} 
                                  alt={entity.itemName || entity.name}
                                  className="w-full h-full object-cover"
                                  onError={(e) => {
                                    // Hide image on error
                                    e.currentTarget.style.display = 'none';
                                  }}
                                />
                              </div>
                            )}
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center gap-2 flex-wrap">
                                <span className="font-medium">{entity.itemNumber}</span>
                                <Badge variant="outline" className="text-xs">
                                  {entity.entityType}
                                </Badge>
                              </div>
                              {/* Display ItemName if available */}
                              {entity.itemName && (
                                <p className="text-sm text-gray-900 mt-1 font-medium truncate">
                                  {entity.itemName}
                                </p>
                              )}
                              {/* Display name as fallback */}
                              {!entity.itemName && entity.name && (
                                <p className="text-sm text-gray-600 mt-1 truncate">{entity.name}</p>
                              )}
                            </div>
                            {/* InRiver Link Button */}
                            <Button
                              variant="ghost"
                              size="sm"
                              className="flex-shrink-0"
                              onClick={(e) => {
                                e.stopPropagation();
                                window.open(
                                  `https://portal2-prod1a-euw.productmarketingcloud.com/app/enrich#/entity/${entity.entityId}`,
                                  '_blank'
                                );
                              }}
                            >
                              <ExternalLink className="w-4 h-4" />
                            </Button>
                          </div>
                        ))}
                      </div>
                    </ScrollArea>
                  )}
                </CardContent>
              </Card>
            </div>
          </TabsContent>

          {/* Templates Tab */}
          <TabsContent value="templates" className="space-y-4">
            <Card>
              <CardHeader>
                <div className="flex items-center justify-between">
                  <CardTitle>PowerPoint Templates</CardTitle>
                  {templates.length > 0 && (
                    <Badge variant="outline">
                      {selectedTemplateId ? `1 selected` : `${templates.length} available`}
                    </Badge>
                  )}
                </div>
                {templates.length > 0 && (
                  <Input
                    placeholder="Search templates by name, description, or tags..."
                    value={templateSearchTerm}
                    onChange={(e) => setTemplateSearchTerm(e.target.value)}
                    className="max-w-md mt-4"
                  />
                )}
              </CardHeader>
              <CardContent className="space-y-4">
                {/* Upload Area */}
                <div className="border-2 border-dashed border-gray-300 rounded-lg p-8 text-center">
                  <Upload className="w-12 h-12 mx-auto mb-4 text-gray-400" />
                  <Label
                    htmlFor="template-upload"
                    className="text-blue-600 cursor-pointer hover:underline text-lg"
                  >
                    {uploadingTemplate ? 'Uploading...' : 'Upload New Template'}
                  </Label>
                  <p className="text-sm text-gray-500 mt-2">
                    Only .pptx files are supported (max 50MB)
                  </p>
                  <Input
                    id="template-upload"
                    type="file"
                    accept=".pptx"
                    onChange={handleFileSelect}
                    disabled={uploadingTemplate}
                    className="hidden"
                  />
                </div>

                {/* Templates List */}
                {loadingTemplates ? (
                  <div className="flex items-center justify-center p-8">
                    <Loader2 className="w-6 h-6 animate-spin" />
                  </div>
                ) : getFilteredTemplates().length > 0 ? (
                  <ScrollArea className="h-96">
                    <div className="space-y-2">
                      {getFilteredTemplates().map((template) => (
                        <div
                          key={template.id}
                          className={cn(
                            "flex items-center gap-3 p-4 rounded-lg border-2 transition-all cursor-pointer",
                            selectedTemplateId === template.id
                              ? 'border-blue-500 bg-blue-50'
                              : 'border-gray-200 hover:border-gray-300'
                          )}
                          onClick={() => handleSelectTemplate(template.id)}
                        >
                          <Checkbox
                            checked={selectedTemplateId === template.id}
                            onCheckedChange={() => handleSelectTemplate(template.id)}
                            onClick={(e) => e.stopPropagation()}
                          />
                          <FileText className="w-8 h-8 text-blue-500 flex-shrink-0" />
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2">
                              <h3 className="font-medium truncate">{template.name}</h3>
                              {template.usageCount > 0 && (
                                <Badge variant="secondary" className="text-xs">
                                  Used {template.usageCount}x
                                </Badge>
                              )}
                            </div>
                            {template.description && (
                              <p className="text-sm text-gray-600 truncate">{template.description}</p>
                            )}
                            <div className="flex items-center gap-2 mt-1">
                              <span className="text-xs text-gray-500">
                                {(template.fileSize / 1024 / 1024).toFixed(2)} MB
                              </span>
                              <span className="text-xs text-gray-400">•</span>
                              <span className="text-xs text-gray-500">
                                {new Date(template.uploadedAt).toLocaleDateString()}
                              </span>
                            </div>
                            {template.tags && template.tags.length > 0 && (
                              <div className="flex gap-1 mt-2 flex-wrap">
                                {template.tags.map(tag => (
                                  <Badge key={tag} variant="outline" className="text-xs">
                                    {tag}
                                  </Badge>
                                ))}
                              </div>
                            )}
                          </div>
                          <div className="flex items-center gap-2 flex-shrink-0">
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={(e) => {
                                e.stopPropagation();
                                window.open(template.blobUrl, '_blank');
                              }}
                            >
                              <Download className="w-4 h-4" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              className="text-red-600 hover:text-red-700"
                              onClick={(e) => {
                                e.stopPropagation();
                                handleDeleteTemplate(template);
                              }}
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </div>
                        </div>
                      ))},
                    </div>
                  </ScrollArea>
                ) : (
                  <div className="text-center p-8 text-gray-500">
                    <FileText className="w-12 h-12 mx-auto mb-3 text-gray-300" />
                    <p>No templates found</p>
                    {templateSearchTerm && (
                      <p className="text-sm mt-2">Try adjusting your search</p>
                    )}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Generate Tab */}
          <TabsContent value="generate" className="space-y-4">
            <Card>
              <CardHeader>
                <CardTitle>Generate Catalog</CardTitle>
              </CardHeader>
              <CardContent className="space-y-6">
                {/* Summary */}
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div className="p-4 bg-blue-50 rounded-lg">
                    <p className="text-sm text-gray-600 mb-1">Selected Items</p>
                    <p className="text-2xl font-semibold text-blue-600">{selectedCount}</p>
                  </div>
                  <div className="p-4 bg-purple-50 rounded-lg">
                    <p className="text-sm text-gray-600 mb-1">Selected Template</p>
                    {selectedTemplateId ? (
                      <div>
                        <p className="text-sm font-semibold text-purple-600 truncate">
                          {templates.find(t => t.id === selectedTemplateId)?.name || 'Unknown'}
                        </p>
                        <p className="text-xs text-purple-500 mt-1">
                          {templates.find(t => t.id === selectedTemplateId)?.usageCount} uses
                        </p>
                      </div>
                    ) : (
                      <p className="text-sm font-medium text-gray-400">None selected</p>
                    )}
                  </div>
                  <div className="p-4 bg-green-50 rounded-lg">
                    <p className="text-sm text-gray-600 mb-1">Source Query</p>
                    <p className="text-sm font-medium text-green-600 truncate">
                      {selectedQuery || 'None'}
                    </p>
                  </div>
                </div>

                {/* Selected Items Preview */}
                {selectedCount > 0 && (
                  <div>
                    <h3 className="font-medium mb-3">Selected Items Preview</h3>
                    <ScrollArea className="h-64 border rounded-lg p-3">
                      <div className="space-y-2">
                        {entities
                          .filter(e => e.isSelected)
                          .map((entity) => (
                            <div
                              key={entity.entityId}
                              className="flex items-center justify-between p-2 bg-gray-50 rounded"
                            >
                              <div>
                                <span className="font-medium">{entity.itemNumber}</span>
                                <p className="text-sm text-gray-600">{entity.name}</p>
                              </div>
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => toggleEntitySelection(entity.entityId)}
                              >
                                <Trash2 className="w-4 h-4 text-red-500" />
                              </Button>
                            </div>
                          ))},
                      </div>
                    </ScrollArea>
                  </div>
                )}

                {/* Generate Button */}
                <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                  <div>
                    <p className="font-medium">Ready to generate?</p>
                    <p className="text-sm text-gray-600">
                      {selectedCount === 0
                        ? 'Select items from the Browse tab first'
                        : !selectedTemplateId
                        ? 'Select a template from the Templates tab'
                        : `Generate a PowerPoint catalog with ${selectedCount} items`}
                    </p>
                  </div>
                  <CapabilityButton capability={P.ProductPdfJobCreate}
                    className="bg-blue-600 hover:bg-blue-700"
                    onClick={handleGenerateCatalog}
                    disabled={selectedCount === 0 || !selectedTemplateId || generatingCatalog}
                  >
                    {generatingCatalog ? (
                      <>
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                        Generating...
                      </>
                    ) : (
                      <>
                        <Play className="w-4 h-4 mr-2" />
                        Generate Catalog
                      </>
                    )}
                  </CapabilityButton>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Jobs Tab */}
          <TabsContent value="jobs" className="space-y-4">
            <Card>
              <CardHeader>
                <div className="flex items-center justify-between">
                  <CardTitle className="flex items-center gap-2">
                    <Clock className="w-5 h-5" />
                    Catalog Generation Jobs
                  </CardTitle>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={loadCatalogJobs}
                    disabled={loadingJobs}
                  >
                    {loadingJobs ? (
                      <Loader2 className="w-4 h-4 animate-spin" />
                    ) : (
                      <>
                        <RefreshCw className="w-4 h-4 mr-1" />
                        Refresh
                      </>
                    )}
                  </Button>
                </div>
              </CardHeader>
              <CardContent>
                {catalogJobs.length === 0 ? (
                  <div className="text-center p-12 text-gray-500">
                    <Package className="w-12 h-12 mx-auto mb-3 text-gray-300" />
                    <p>No catalog jobs yet</p>
                    <p className="text-sm mt-2">Generated catalogs will appear here</p>
                  </div>
                ) : (
                  <ScrollArea className="h-[600px]">
                    <div className="space-y-3">
                      {catalogJobs.map((job) => (
                        <Card key={job.id} className="overflow-hidden">
                          <CardContent className="p-4">
                            <div className="space-y-3">
                              {/* Header */}
                              <div className="flex items-start justify-between">
                                <div className="flex-1">
                                  <div className="flex items-center gap-2 mb-1">
                                    {getStatusIcon(job.status)}
                                    <h3 className="font-medium">{job.name}</h3>
                                    <Badge variant={getStatusBadgeVariant(job.status)}>
                                      {job.status}
                                    </Badge>
                                  </div>
                                  <p className="text-sm text-gray-600">
                                    Query: {job.queryName} - {job.itemCount} items
                                  </p>
                                </div>
                                <div className="flex items-center gap-2">
                                  {job.status === 'completed' && job.outputUrl && (
                                    <Button
                                      variant="outline"
                                      size="sm"
                                      onClick={() => handleDownloadCatalog(job)}
                                    >
                                      <Download className="w-4 h-4 mr-1" />
                                      Download
                                    </Button>
                                  )}
                                  {(job.status === 'completed' || job.status === 'failed') && (
                                    <CapabilityButton capability={P.ProductPdfJobDelete}
                                      variant="outline"
                                      size="sm"
                                      className="text-red-600 hover:text-red-700"
                                      onClick={() => handleDeleteJob(job)}
                                    >
                                      <Trash2 className="w-4 h-4" />
                                    </CapabilityButton>
                                  )}
                                </div>
                              </div>

                              {/* Progress Bar */}
                              {(job.status === 'processing' || job.status === 'pending') && (
                                <div className="space-y-1">
                                  <Progress value={job.progress} className="h-2" />
                                  <p className="text-xs text-gray-500">
                                    {job.progress}% complete
                                  </p>
                                </div>
                              )}

                              {/* Stats */}
                              <div className="flex items-center gap-4 text-sm">
                                <div className="flex items-center gap-1">
                                  <CheckCircle className="w-4 h-4 text-green-500" />
                                  <span>{job.successCount} success</span>
                                </div>
                                {job.errorCount > 0 && (
                                  <div className="flex items-center gap-1">
                                    <XCircle className="w-4 h-4 text-red-500" />
                                    <span>{job.errorCount} errors</span>
                                  </div>
                                )}
                                <div className="text-gray-500">
                                  Created: {new Date(job.createdAt).toLocaleString()}
                                </div>
                                {job.completedAt && (
                                  <div className="text-gray-500">
                                    Completed: {new Date(job.completedAt).toLocaleString()}
                                  </div>
                                )}
                              </div>
                            </div>
                          </CardContent>
                        </Card>
                      ))},
                    </div>
                  </ScrollArea>
                )}
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>
      
      {/* Upload Template Dialog */}
      {showUploadDialog && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <Card className="w-full max-w-2xl max-h-[90vh] flex flex-col">
            <CardHeader className="border-b">
              <div className="flex items-center justify-between">
                <CardTitle className="flex items-center gap-2">
                  <Upload className="w-5 h-5" />
                  Upload Template
                </CardTitle>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={handleCancelUpload}
                  disabled={uploadingTemplate}
                >
                  <X className="w-4 h-4" />
                </Button>
              </div>
            </CardHeader>
            <ScrollArea className="flex-1">
              <CardContent className="space-y-4 pt-6">
                {/* File Info */}
                {uploadForm.file && (
                  <Alert className="bg-blue-50 border-blue-200">
                    <FileText className="h-4 w-4 text-blue-600" />
                    <AlertDescription className="text-blue-800">
                      <div className="flex items-center justify-between">
                        <div>
                          <strong>{uploadForm.file.name}</strong>
                          <p className="text-sm mt-1">
                            {(uploadForm.file.size / 1024 / 1024).toFixed(2)} MB
                          </p>
                        </div>
                      </div>
                    </AlertDescription>
                  </Alert>
                )}
                
                {/* Name Field */}
                <div className="space-y-2">
                  <Label htmlFor="template-name" className="text-sm font-medium">
                    Template Name <span className="text-red-500">*</span>
                  </Label>
                  <Input
                    id="template-name"
                    placeholder="e.g., Product Catalog 2024"
                    value={uploadForm.name}
                    onChange={(e) => setUploadForm(prev => ({ ...prev, name: e.target.value }))}
                    disabled={uploadingTemplate}
                    className="w-full"
                  />
                  <p className="text-xs text-gray-500">
                    A descriptive name to identify this template
                  </p>
                </div>
                
                {/* Description Field */}
                <div className="space-y-2">
                  <Label htmlFor="template-description" className="text-sm font-medium">
                    Description
                  </Label>
                  <Textarea
                    id="template-description"
                    placeholder="e.g., Standard product catalog template with company branding and product showcase slides"
                    value={uploadForm.description}
                    onChange={(e) => setUploadForm(prev => ({ ...prev, description: e.target.value }))}
                    disabled={uploadingTemplate}
                    rows={3}
                    className="w-full resize-none"
                  />
                  <p className="text-xs text-gray-500">
                    Optional description to help identify the purpose of this template
                  </p>
                </div>
                
                {/* Tags Field */}
                <div className="space-y-2">
                  <Label htmlFor="template-tags" className="text-sm font-medium">
                    Tags
                  </Label>
                  <div className="flex gap-2">
                    <Input
                      id="template-tags"
                      placeholder="e.g., product, catalog, 2024"
                      value={tagInput}
                      onChange={(e) => setTagInput(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') {
                          e.preventDefault();
                          handleAddTag();
                        }
                      }}
                      disabled={uploadingTemplate}
                      className="flex-1"
                    />
                    <Button
                      type="button"
                      variant="outline"
                      onClick={handleAddTag}
                      disabled={uploadingTemplate || !tagInput.trim()}
                    >
                      <Plus className="w-4 h-4 mr-1" />
                      Add
                    </Button>
                  </div>
                  
                  {/* Tags Display */}
                  {uploadForm.tags.length > 0 && (
                    <div className="flex flex-wrap gap-2 mt-2">
                      {uploadForm.tags.map(tag => (
                        <Badge
                          key={tag}
                          variant="secondary"
                          className="flex items-center gap-1 pl-2 pr-1"
                        >
                          {tag}
                          <button
                            onClick={() => handleRemoveTag(tag)}
                            disabled={uploadingTemplate}
                            className="ml-1 hover:bg-gray-300 rounded-full p-0.5"
                          >
                            <X className="w-3 h-3" />
                          </button>
                        </Badge>
                      ))},
                    </div>
                  )}
                  
                  <p className="text-xs text-gray-500">
                    Add tags to help organize and search for templates
                  </p>
                </div>
                
                {/* Info Alert */}
                <Alert>
                  <AlertCircle className="h-4 w-4" />
                  <AlertDescription>
                    <ul className="list-disc list-inside space-y-1 text-sm">
                      <li>Only .pptx files are supported</li>
                      <li>Maximum file size: 50MB</li>
                      <li>Template will be stored securely in Azure Blob Storage</li>
                      <li>Usage will be tracked automatically when generating catalogs</li>
                    </ul>
                  </AlertDescription>
                </Alert>
              </CardContent>
            </ScrollArea>
            
            {/* Dialog Footer */}
            <div className="border-t p-4 flex justify-end gap-2">
              <Button
                variant="outline"
                onClick={handleCancelUpload}
                disabled={uploadingTemplate}
              >
                Cancel
              </Button>
              <CapabilityButton capability={P.ProductCatalogTemplateUpload}
                onClick={handleUploadTemplate}
                disabled={uploadingTemplate || !uploadForm.file || !uploadForm.name.trim()}
                className="bg-blue-600 hover:bg-blue-700"
              >
                {uploadingTemplate ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Uploading...
                  </>
                ) : (
                  <>
                    <Upload className="w-4 h-4 mr-2" />
                    Upload Template
                  </>
                )}
              </CapabilityButton>
            </div>
          </Card>
        </div>
      )}
    </div>
  );
}
